import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Film, RefreshCw } from 'lucide-react';
import {
  wanVideoModelControl,
  isApiError,
  WanVideoModelCatalogItem,
  WanVideoModelControlConflictResponse,
  WanVideoModelControlResponse,
  WanVideoModelSelection,
} from '../api/client';

const NO_OVERRIDE_VALUE = '__no_override__';

function selectionValue(selection: WanVideoModelSelection): string {
  return selection.mode === 'no_override' ? NO_OVERRIDE_VALUE : selection.modelId;
}

function formatCost(model: WanVideoModelCatalogItem): string {
  if (model.costStatus !== 'confirmed' || model.costPerSecondUsd === null) {
    return 'cost unconfirmed';
  }
  const decimalPlaces = Number.isInteger(model.costPerSecondUsd * 100) ? 2 : 3;
  return `$${model.costPerSecondUsd.toFixed(decimalPlaces)}/sec`;
}

function isConflictPayload(value: unknown): value is WanVideoModelControlConflictResponse {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<WanVideoModelControlConflictResponse>;
  return candidate.error?.code === 'revision_conflict'
    && candidate.error.status === 409
    && Array.isArray(candidate.catalog)
    && typeof candidate.state?.revision === 'number'
    && typeof candidate.state.selection === 'object';
}

export default function WanVideoModelControl() {
  const [data, setData] = useState<WanVideoModelControlResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await wanVideoModelControl.get());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load Wan video model control');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleChange = async (value: string) => {
    if (!data || saving || value === selectionValue(data.state.selection)) return;

    const selection: WanVideoModelSelection = value === NO_OVERRIDE_VALUE
      ? { mode: 'no_override' }
      : { mode: 'model', modelId: value };

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await wanVideoModelControl.update(selection, data.state.revision);
      setData(updated);
      setSuccess('Applied. The next Atlas video generation will use this selection.');
    } catch (updateError) {
      const message = updateError instanceof Error ? updateError.message : 'Failed to update model selection';
      if (isApiError<WanVideoModelControlConflictResponse>(updateError, 409)
        && isConflictPayload(updateError.body)) {
        setData({ catalog: updateError.body.catalog, state: updateError.body.state });
        setError(updateError.body.error.message);
        return;
      }
      try {
        setData(await wanVideoModelControl.get());
        setError(`${message} Current server state was reloaded; choose again.`);
      } catch (reloadError) {
        const reloadMessage = reloadError instanceof Error ? reloadError.message : 'reload failed';
        setData(null);
        setError(`${message} The current server state could not be reloaded: ${reloadMessage}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const activeSelection = data?.state.selection;
  const selectedModel = activeSelection?.mode === 'model'
    ? data?.catalog.find(model => model.id === activeSelection.modelId) ?? null
    : null;

  return (
    <section className="card-glow rounded-2xl p-6 animate-fade-in-up border border-violet-500/20">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-500/15 text-violet-400 flex items-center justify-center flex-shrink-0">
            <Film className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Live Wan video model</h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Overrides only Atlas video requests. Changes apply to the next phone generation without an app or Worker deploy.
            </p>
          </div>
        </div>

        <div className="w-full lg:w-[28rem]">
          {loading ? (
            <div className="input-neural w-full px-4 py-3 rounded-xl text-sm flex items-center gap-2 text-muted-foreground">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Loading model control…
            </div>
          ) : data ? (
            <select
              value={selectionValue(data.state.selection)}
              onChange={(event) => void handleChange(event.target.value)}
              disabled={saving}
              aria-label="Live Wan video model"
              className="input-neural w-full px-4 py-3 rounded-xl text-sm appearance-none cursor-pointer disabled:opacity-60 disabled:cursor-wait"
            >
              <option value={NO_OVERRIDE_VALUE}>No override — use the iOS-sent model</option>
              {data.catalog.map(model => (
                <option key={model.id} value={model.id}>
                  {model.displayName} — {formatCost(model)}
                </option>
              ))}
            </select>
          ) : (
            <button onClick={() => void load()} className="btn-secondary w-full px-4 py-3 rounded-xl text-sm flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Retry loading model control
            </button>
          )}
          {saving && <p className="text-xs text-violet-300 mt-2">Applying selection…</p>}
        </div>
      </div>

      {selectedModel && (
        <div className="mt-5 rounded-xl bg-secondary/50 border border-border/50 p-4 text-sm">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <span><span className="text-muted-foreground">Provider cost:</span> {formatCost(selectedModel)}</span>
            <span><span className="text-muted-foreground">Durations:</span> {selectedModel.supportedDurations.join(', ')}s</span>
            <span><span className="text-muted-foreground">Resolutions:</span> {selectedModel.supportedResolutions.join(', ')}</span>
            <span>
              <span className="text-muted-foreground">DashScope fallback:</span>{' '}
              {selectedModel.dashScopeFallbackAvailable ? 'exact mapping available' : 'unavailable'}
            </span>
          </div>
          {(selectedModel.notes.length > 0 || selectedModel.atlasFieldAcceptance === 'unconfirmed') && (
            <div className="mt-3 flex items-start gap-2 text-amber-300">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                {selectedModel.notes.length > 0 && <p>{selectedModel.notes.join(' ')}</p>}
                {selectedModel.atlasFieldAcceptance === 'unconfirmed' && (
                  <p>Live Atlas field acceptance is not yet confirmed.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {data?.state.selection.mode === 'no_override' && (
        <p className="mt-4 text-sm text-muted-foreground">
          No override is active. Atlas receives the model sent by iOS, matching the pre-control behavior.
        </p>
      )}

      {success && (
        <div className="mt-4 flex items-center gap-2 text-sm text-emerald-300">
          <CheckCircle2 className="w-4 h-4" />
          {success}
        </div>
      )}
      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-rose-500/10 border border-rose-500/30 p-3 text-sm text-rose-300">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </section>
  );
}
